import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API_BASE, getToken, type Expense } from "../api";
import { useAuth } from "../auth";
import { fmtDate } from "../ui";

const LABEL = {
  submitted: "Soumise",
  taken_into_account: "Prise en compte",
  rejected: "Rejetée",
} as const;

const BADGE_CLASS = {
  submitted: "review",
  taken_into_account: "validated",
  rejected: "pending",
} as const;

/* Calcule la date d'auto-suppression : 15 du mois suivant la prise en compte */
function purgeDate(takenAt: number): Date {
  const d = new Date(takenAt);
  return new Date(d.getFullYear(), d.getMonth() + 1, 15);
}

export default function ExpenseView() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [exp, setExp] = useState<Expense | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const reuploadRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    const r = await api<{ expense: Expense }>(`/expenses/${id}`);
    setExp(r.expense);
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (!exp?.r2_key) { setPhotoUrl(null); return; }
    let url: string | null = null;
    (async () => {
      const res = await fetch(`${API_BASE}/expenses/${exp.id}/file`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.ok) {
        const blob = await res.blob();
        url = URL.createObjectURL(blob);
        setPhotoUrl(url);
      }
    })();
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [exp?.id, exp?.r2_key, exp?.updated_at]);

  if (!exp || !user) return <p className="muted">Chargement…</p>;

  const isAdmin = user.role === "admin";
  const isAuthor = exp.user_id === user.id;
  const canDecide = !isAuthor && exp.status !== "taken_into_account"; // tout user ≠ auteur peut cocher / rejeter
  const canUncheck = exp.status === "taken_into_account" && (exp.decided_by === user.id || isAdmin);
  const canResubmit = isAuthor && exp.status === "rejected";
  const canDelete = isAdmin || (isAuthor && exp.status !== "taken_into_account");

  async function takeIntoAccount() {
    const note = prompt("Commentaire (optionnel) :") || "";
    await api(`/expenses/${exp!.id}/decision`, {
      method: "POST",
      json: { decision: "taken_into_account", note: note || undefined },
    });
    await reload();
  }

  async function reject() {
    const note = prompt("Motif du rejet (obligatoire — ex. \"photo illisible\") :");
    if (!note?.trim()) return;
    await api(`/expenses/${exp!.id}/decision`, {
      method: "POST",
      json: { decision: "rejected", note },
    });
    await reload();
  }

  async function uncheck() {
    if (!confirm("Décocher \"prise en compte\" ?")) return;
    await api(`/expenses/${exp!.id}/uncheck`, { method: "POST" });
    await reload();
  }

  async function reuploadPhoto(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const buf = await f.arrayBuffer();
    await fetch(`${API_BASE}/expenses/${exp!.id}/file`, {
      method: "PUT",
      headers: {
        "Content-Type": f.type || "image/jpeg",
        Authorization: `Bearer ${getToken()}`,
      },
      body: buf,
    });
    await reload();
  }

  async function del() {
    if (!confirm("Supprimer définitivement cette note ?")) return;
    await api(`/expenses/${exp!.id}`, { method: "DELETE" });
    nav("/expenses", { replace: true });
  }

  const purge = exp.taken_into_account_at ? purgeDate(exp.taken_into_account_at) : null;

  return (
    <>
      <div className="row spread" style={{ alignItems: "flex-start" }}>
        <h2 style={{ margin: 0, flex: 1 }}>{exp.label}</h2>
        <span className={`badge ${BADGE_CLASS[exp.status]}`}>{LABEL[exp.status]}</span>
      </div>
      <div className="row wrap" style={{ gap: 6, margin: "6px 0 14px" }}>
        <span className="entity-chip">{exp.entity_code}</span>
        <span className="tiny">{exp.user_identifier}</span>
        <span className="tiny">· {fmtDate(exp.expense_date)}</span>
        <span className="tiny">· <b>{(exp.amount_cents / 100).toFixed(2)} {exp.currency}</b></span>
      </div>

      {exp.category && <div className="muted" style={{ marginBottom: 10 }}>Catégorie : {exp.category}</div>}
      {exp.notes && (
        <div className="card">
          <div className="section-h" style={{ margin: "0 0 6px" }}>Notes</div>
          <div style={{ whiteSpace: "pre-wrap" }}>{exp.notes}</div>
        </div>
      )}

      {photoUrl ? (
        <div className="card" style={{ padding: 4 }}>
          <img src={photoUrl} alt="justificatif" style={{ width: "100%", borderRadius: "var(--r-sm)", display: "block" }} />
        </div>
      ) : (
        <p className="muted">Pas de justificatif joint.</p>
      )}

      {exp.decision_note && (
        <div
          className="card"
          style={{
            background: exp.status === "rejected" ? "var(--terra-soft)" : "var(--blue-soft)",
          }}
        >
          <div className="section-h" style={{ margin: 0 }}>
            {exp.status === "rejected" ? "Motif de rejet" : "Commentaire"}
          </div>
          <div style={{ marginTop: 6 }}>{exp.decision_note}</div>
          <div className="tiny" style={{ marginTop: 6 }}>
            {exp.decided_by_name} · {exp.decided_at ? fmtDate(exp.decided_at) : ""}
          </div>
        </div>
      )}

      {exp.status === "taken_into_account" && purge && (
        <div className="muted tiny" style={{ marginTop: 10 }}>
          Cette note s'effacera automatiquement le {purge.toLocaleDateString("fr-FR", {
            day: "2-digit", month: "long", year: "numeric"
          })}.
        </div>
      )}

      {/* Actions */}
      {canDecide && (
        <div className="row" style={{ gap: 8, marginTop: 14, flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={takeIntoAccount}>
            ☐ Prise en compte
          </button>
          <button className="btn btn-ghost" onClick={reject}>
            Rejeter
          </button>
        </div>
      )}

      {canUncheck && (
        <div style={{ marginTop: 14 }}>
          <button className="btn btn-ghost full" onClick={uncheck}>
            ☑ Décocher « prise en compte »
          </button>
        </div>
      )}

      {canResubmit && (
        <div style={{ marginTop: 14 }}>
          <p className="muted" style={{ marginBottom: 6 }}>
            Cette note a été rejetée. Tu peux refaire la photo et la re-soumettre :
          </p>
          <input
            ref={reuploadRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={reuploadPhoto}
            style={{ display: "none" }}
          />
          <button className="btn btn-secondary full" onClick={() => reuploadRef.current?.click()}>
            📷 Re-prendre la photo
          </button>
        </div>
      )}

      {canDelete && (
        <>
          <div style={{ height: 30 }} />
          <button
            className="btn btn-ghost full"
            onClick={del}
            style={{ color: "var(--terra-deep)" }}
          >
            Supprimer
          </button>
        </>
      )}
    </>
  );
}
